import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

import "../../styles/css/disclosures.css";
import { FIG_MAILING_ADDRESS } from "../../helpers/contact_helper";

const CcpaOptOut = ({ handleCookiePreferencesPopUpButtonClick }) => {
  return (
    <div>
      <div className="glba-header">
        <h1 className="glba-title">California Privacy Statement</h1>
      </div>
      <Helmet>
        <title>Fig Loans | California Privacy Statement</title>
        <meta
          name="description"
          content="Fig website terms and conditions of use"
        />
        <meta
          property="og:description"
          content="Fig website terms and conditions of use"
        />
      </Helmet>
      <div className="disclosurewrap">
        <p className="disc-para">
          <span className="bold-accent">Last Revision Date:</span> June 28, 2023
        </p>
        <p className="disc-para">
          Your privacy is important to us. This California Privacy Statement explains
          how Fig collects, uses, and discloses personal information relating to
          <strong> California residents</strong> covered by the California Consumer Privacy Act of 2018,
          as amended by the California Privacy Rights Act of 2020 (collectively, the “CCPA”).
          This “Statement” constitutes our notice at collection and our privacy policy
          pursuant to the CCPA.
        </p>
        <p className="disc-para">
          <span className="bold-accent">Applicability</span>
        </p>
        <p className="disc-para">
          The CCPA does not apply to certain personal information, such as:
          <ul>
            <li>
              Personal information covered under certain federal or state financial
              privacy laws, including but not limited to the Gramm-Leach-Bliley Act,
              the Fair Credit Reporting Act, and the California Financial Information
              Privacy Act.
            </li>
            <li>
              Publicly available information from government records.
            </li>
          </ul>
        </p>
        <p className="disc-para">
          The specific Personal Information that we collect, use, and disclose relating
          to a California resident covered by the CCPA varies based on the relationship
          between Fig and the individual. To learn more about how we collect, disclose
          and secure information for these customers, please see our{" "}
          <Link
            to="privacy-glba"
            className="ccpa-policy-link"
          >
            GLBA Financial Privacy Notice
          </Link>
          {" "}and{" "}
          <Link
            to="privacy"
            className="ccpa-policy-link"
          >
            Privacy Policy
          </Link>
          .
        </p>
        <p className="disc-para">
          <span className="bold-accent">Securing Personal Information</span>
        </p>
        <p className="disc-para">
          Fig is committed to keeping Personal Information secure. In addition to meeting
          our obligations under applicable laws and regulations, we have implemented
          physical, technical, electronic, procedural and organizational safeguards and
          security measures to protect personal data against accidental, unlawful, or
          unauthorized destruction, loss, alteration, disclosure, or access.
        </p>
        <p className="disc-para-title">
          Collection, Use and Disclosure of Personal Information
        </p>
        <p className="disc-para">
          In the past 12 months, we have collected and disclosed for our business purposes
          each of the following categories of Personal Information relating to California
          residents covered by this Notice:
          <ul>
            <li>
              <span className="bold-accent">Identifiers.</span>This may include a real
              name, alias, postal address, unique personal identifier, online identifier,
              Internet Protocol (IP) address, email address, account name, Social Security
              number, driver's license number, passport number, or other similar identifiers.
            </li>
            <li>
              <span className="bold-accent">
                Personal information described in the California Customer Records Statute
                (Cal. Civ. Code § 1798.80(e)).
              </span>
              This category is similar to Identifiers and includes a name, signature,
              Social Security number, physical characteristics or description, address,
              telephone number, passport number, driver's license or state identification
              card number, insurance policy number, education, employment, employment history,
              bank account number, credit card number, debit card number, or any other financial
              information, medical information, or health insurance information.
            </li>
            <li>
              <span className="bold-accent">
                Internet or other similar network activity.
              </span>
              This may include browsing history, search history, or information on a consumer's
              interaction with a website, application, or advertisement, such as your clicks
              and how long you are using our website.
            </li>
          </ul>
        </p>

        <p className="disc-para">
          In the past 12 months, we have collected and disclosed for our business purposes each of
          the following categories of Sensitive Personal Information relating to California
          residents covered by this Notice:
          <ul>
            <li>
              Personal Information that reveals:
              <li>
                A California resident's Social Security, driver's license, state identification
                card, or passport number; and,
              </li>
              <li>
                A California resident's account log-in, financial account, debit card, or credit
                card number in combination with any required security or access code, password, or
                credentials allowing access to an account.
              </li>
            </li>
          </ul>
        </p>
        <p className="disc-para">
          In the past 12 months, we have collected Personal Information from the following categories
          of sources:

          <ul>
            <li>
              Directly from you or your authorized agents. For example, from forms you complete on your
              application or through information you provide us during the life of your loan.
            </li>
            <li>
              Indirectly from you. For example, from observing your actions on our website (www.figloans.com)
            </li>
            <li>
              From third-parties that interact with us in connection with the origination or servicing of
              your loan. For example, from credit bureaus, affiliates, or other companies.
            </li>
          </ul>
        </p>
        <p className="disc-para">
          We may disclose your Personal Information to service providers and third parties in order to carry out
          specific business or commercial purposes. In the preceding 12 months, we have disclosed your personal
          information to the following third parties:
          <ul>
            <li>
              Vendors and service providers who provide services such as banking and communication infrastructure,
              operating systems and platforms, website hosting, storage, legal expertise, tax expertise, information
              technology, data analytics, customer service.
            </li>
            <li>
              Partners and third-party service providers who provide services such as payments, advertisements,
              services and products.
            </li>
            <li>
              Government agencies as required by law.
            </li>
          </ul>
        </p>
        <p className="disc-para">
          We collect, use and disclose for our business purposes Personal Information, including Sensitive Personal
          Information, relating to California residents to operate, manage, and maintain our business, to provide
          our products and services, and to accomplish our business or commercial purposes, including the following:
          <ul>
            <li>
              <span className="bold-accent">Account Services </span>including:
              (1) establishing, maintaining, supporting, and servicing an account you may have opened with us and
              for which you provided the information or that you may have applied for or established with us; (2)
              providing services, products, or information you may have requested from us; and (3) performing
              services such as maintaining or servicing accounts, providing customer service, processing or
              fulfilling orders and transactions, verifying customer information, processing payments, providing
              financing, providing analytic services, or providing similar services on our own behalf or on our
              service provider’s behalf.
            </li>
            <li>
              <span className="bold-accent">Security and Fraud Detection </span>
              including detecting security incidents and protecting against malicious, deceptive, fraudulent,
              or illegal activity.
            </li>
            <li>
              <span className="bold-accent">Debugging </span>including identifying and correcting errors to our
              system and services.
            </li>
            <li>
              <span className="bold-accent">
                Improvement of Products and Services{" "}
              </span>
              such as enhancing the quality of our products and services.
            </li>
            <li>
              <span className="bold-accent">Internal Research </span>including
              technological development and demonstration.
            </li>
            <li>
              <span className="bold-accent">
                Advertising and Marketing Services{" "}
              </span>
              including advertising or marketing services on our own behalf or
              on behalf of our affiliates.
            </li>
            <li>
              <span className="bold-accent">
                To Comply with our Legal Obligations{" "}
              </span>
            </li>
            <li>
              <span className="bold-accent">Audits </span>including reviewing
              current interactions with you and related transactions.
            </li>
            <li>
              <span className="bold-accent">
                For Other Business or Commercial Purposes{" "}
              </span>
              as permitted by law.
            </li>
          </ul>
        </p>
        <p className="disc-para">
          <span className="bold-accent">How Long We Retain Personal Information</span>
        </p>
        <p className="disc-para">
          The length of time that we intend to retain each category of Personal Information
          will depend on a number of criteria, such as the length of time we are required
          to retain Personal Information in order to comply with applicable legal and regulatory
          requirements; the length of time we may need to accomplish the business purpose(s)
          for which such Personal Information is collected, used or disclosed (as indicated in
          this Notice), and whether you exercise your rights under the CCPA (subject to certain
          exceptions) to request that your Personal Information is deleted.
        </p>
        <p className="disc-para">
          <span className="bold-accent">Sale or Sharing of Personal Information</span>
        </p>
        <p className="disc-para">
          For purposes of this Notice:
          <ul>
            <li>
              “sold” means the disclosure of Personal Information or Sensitive Personal Information
              to a third party for monetary or other valuable consideration; and
            </li>
            <li>
              “shared” means the disclosure of Personal Information or Sensitive Personal Information
              to a third party for cross-context behavioral advertising.
            </li>
          </ul>
        </p>
        <p className="disc-para">
          In the 12 months preceding the date of this Notice, we “shared” Personal Information for
          cross-context behavioral advertising, with respect to California residents covered by CCPA to
          deliver advertising and marketing. Fig “shared” Personal Information with our third party
          advertising and marketing vendors who were prohibited from using that Personal Information for
          any purpose other than to provide the specifically requested advertising and marketing services.
        </p>
        <p className="disc-para">
          The categories of Personal Information that we “shared” for cross-context behavioral advertising
          in the 12 months preceding the date of this Notice are:
          <ul>
            <li>
              <span className="bold-accent">Identifiers.</span>This may include a real name, alias, postal
              address, unique personal identifier, online identifier, Internet Protocol (IP) address, email
              address, account name, Social Security number, driver's license number, passport number, or
              other similar identifiers.
            </li>
            <li>
              <span className="bold-accent">Personal information described in the California Customer
                Records Statute (Cal. Civ. Code § 1798.80(e)).</span>This category is similar to Identifiers
              and includes a name, signature, Social Security number, physical characteristics or description,
              address, telephone number, passport number, driver's license or state identification card number,
              insurance policy number, education, employment, employment history, bank account number, credit card
              number, debit card number, or any other financial information, medical information, or health
              insurance information.
            </li>
            <li><span className="bold-accent">Internet or other similar network activity.</span>This may include
              browsing history, search history, or information on a consumer's interaction with a website,
              application, or advertisement, such as your clicks and how long you are using our website.</li>
          </ul>
        </p>
        <p className="disc-para">
          Fig does not sell personal information. Also, it is our business practice not to sell Personal Information
          of minors under 16 years of age and we have no actual knowledge of doing so.
        </p>
        <p className="disc-para">
          You can opt-out of sharing your information by clicking{" "}
          <a
            onClick={handleCookiePreferencesPopUpButtonClick}
            className="ccpa-policy-link pointer"
          >
            here
          </a>
        </p>
        <p className="disc-para">
          <span className="bold-accent">How We Use Sensitive Personal Information</span>
        </p>
        <p className="disc-para">
          We only use or disclose Sensitive Personal Information for the following purposes consistent with CCPA
          Regulations:
          <ul>
            <li>
              <span className="bold-accent">Account Services </span>including: (1) establishing, maintaining, supporting,
              and servicing an account you may have opened with us and for which you provided the information or that
              you may have applied for or established with us; (2) providing services, products, or information you may
              have requested from us; and (3) performing services such as maintaining or servicing accounts, providing
              customer service, processing or fulfilling orders and transactions, verifying customer information,
              processing payments, providing financing, providing analytic services, or providing similar services on our
              own behalf or on our service provider’s behalf.
            </li>
            <li>
              <span className="bold-accent">Security and Fraud Detection </span>including detecting security incidents and
              protecting against malicious, deceptive, fraudulent, or illegal activity.
            </li>
            <li><span className="bold-accent">Debugging </span>including identifying and correcting errors to our system
              and services.
            </li>
            <li><span className="bold-accent">Improvement of Products and Services </span>such as enhancing the quality of our products and services.
            </li>
            <li><span className="bold-accent">Internal Research </span>including technological development and demonstration.
            </li>
            <li><span className="bold-accent">To Comply with our Legal Obligations</span>
            </li>
            <li><span className="bold-accent">Audits </span> including reviewing current interactions with you and related
              transactions.
            </li>
            <li><span className="bold-accent">For Other Business or Commercial Purposes </span>as permitted by law.
            </li>
          </ul>
        </p>
        <p className="disc-para-title">Rights under the CCPA</p>
        <p className="disc-para">
          If you are a California resident covered by the CCPA, you have the right to:
          <ul>
            <li>
              Receive this Notice at or before the point of collection of your Personal Information.
            </li>
            <li>
              Request we disclose to you free of charge the following information covering the 12 months preceding your request:
              <li>the categories of Personal Information about you that we collected;</li>
              <li>the categories of sources from which the Personal Information was collected</li>
              <li>the purpose for collecting Personal Information about you;</li>
              <li>the categories of third parties to whom we disclosed Personal Information about you and the categories of Personal
                Information that were disclosed (if applicable) and the purpose for disclosing the Personal Information about you; and</li>
              <li>the specific pieces of Personal Information we collected about you;</li>
            </li>
            <li>Request we correct inaccurate Personal Information that we maintain about you.</li>
            <li>Request we delete Personal Information we collected from you, unless the CCPA recognizes an exception.</li>
            <li>Be free from unlawful discrimination for exercising your rights under the CCPA.</li>
          </ul>
        </p>
        <p className="disc-para">
          Please see the section below entitled, “How to Exercise Your Rights,” for instructions explaining how you can exercise these
          rights described above.
        </p>
        <p className="disc-para">
          For individuals submitting a request on behalf of another person, we may require proof of authorization and verification of
          identity directly from the person for whom the request is made.
        </p>
        <p className="disc-para">
          For a company or organization submitting a request on behalf of another person, we may require proof of authorization from the
          individual such as a Power of Attorney and verification of identity directly from the person for whom the request is made.
        </p>
        <p className="disc-para">
          In some instances, we may not be able to honor your request. This may occur if we cannot verify your identity or verify that you
          have permission to make the request on behalf of another individual; or the information is exempt from deletion under the
          Gramm-Leach-Bliley Act or the CCPA.
        </p>
        <p className="disc-para">
          <span className="bold-accent">Global Privacy Controls</span>
        </p>
        <p className="disc-para">
          We will recognize global privacy controls as a valid privacy request.
        </p>
        <p className="disc-para-title">How to Exercise Your Rights</p>
        <p className="disc-para">
          You can opt-out of sharing your information by clicking{" "}
          <a
            onClick={handleCookiePreferencesPopUpButtonClick}
            className="ccpa-policy-link pointer"
          >
            here
          </a>
        </p>
        <p className="disc-para">
          If you are a California resident, you may submit a request to know, delete or correct by:
          <p className="left-margin disc-para">
            Completing{" "}
            <Link
              to="ccpa-request-form"
              className="ccpa-policy-link"
            >
              this online form
            </Link>
            <br />
            Emailing{" "}
            <a
              href="mailto:privacy@figloans.com"
              className="ccpa-policy-link"
            >
              privacy@figloans.com<br />
            </a>
            Through your Fig account
          </p>
        </p>
        <p className="disc-para-title">Questions or Concerns</p>
        <p className="disc-para">
          You may contact us with questions or concerns about this Notice and our practices by:
          <p className="left-margin disc-para">
            {FIG_MAILING_ADDRESS.name}<br />
            {FIG_MAILING_ADDRESS.street}<br />
            {FIG_MAILING_ADDRESS.suite}<br />
            {FIG_MAILING_ADDRESS.city}, {FIG_MAILING_ADDRESS.state} {FIG_MAILING_ADDRESS.zip}<br />
            Email us at privacy@figloans.com<br />
          </p>
        </p>
        <p className="disc-para-title">Where You Can Find This Notice Online</p>
        <p className="disc-para">
          You may access this Notice online at https://www.figloans.com/ccpa-opt-out
        </p>
      </div>
    </div>
  );
};

export default CcpaOptOut;